import type { EstimateItemType } from '../types';
import { isPassThroughType } from './passThroughPricing';

/**
 * utils/lineItemPricing.ts — Cómo se le pone precio a UNA partida, según su tipo.
 *
 * POR QUÉ EXISTE:
 *   La app ya había decidido que cada tipo de partida se cotiza con una regla
 *   distinta —material por MARKUP (sobre costo), mano de obra y equipo por
 *   MARGEN (sobre precio), los pase-directo a costo pelado— pero esa decisión
 *   estaba copiada a mano en siete sitios, y solo tres la cumplían:
 *
 *     components/estimate/EstimateToolPanel.tsx      (botones rápidos)
 *     components/estimate/calculators/calcs/*.tsx    (las cuatro calculadoras)
 *     components/estimate/category-tools/CategoryTemplatesView.tsx
 *     components/estimate/PrivateTemplateEditor.tsx
 *     components/estimate/AddMaterialModal.tsx
 *
 *   Los otros cuatro cotizaban el material con `costo / (1 - margen)`. Con el
 *   margen en 30% eso da un markup de 42,857%, que es el número que el
 *   contratista veía en la insignia y no reconocía: no había tecleado 43 en
 *   ninguna parte, y su dial de material decía 20.
 *
 *   Peor que el número: ese 42,857 se guardaba en la lista de precios del
 *   taller al guardar el presupuesto, y desde ahí le ganaba al dial en TODOS
 *   los presupuestos futuros. La contaminación se propagaba sola.
 *
 * LA REGLA, ESCRITA UNA VEZ:
 *   - Fee            → a costo. Un permiso de $400 se remite en $400.
 *   - Material       → costo × (1 + markup)      [MARKUP, sobre el costo]
 *   - Todo lo demás  → costo / (1 - margen)      [MARGEN, sobre el precio]
 *
 * Un 30% de markup y un 30% de margen NO son el mismo dinero: sobre un costo
 * de $100 el primero deja $30 y el segundo $42,86. Por eso la función devuelve
 * SIEMPRE las dos lecturas del mismo precio, y quien pinta elige cuál enseñar
 * —pero ya no puede inventar una tercera.
 */

/** Los dos diales del taller (Ajustes ▸ Precios) de los que sale todo precio. */
export interface LinePricingDials {
    /** Margen objetivo, en porcentaje sobre el PRECIO (0..99). */
    targetMarginPercent: number;
    /** Markup de material por defecto, en porcentaje sobre el COSTO (>= 0). */
    materialMarkupPercent: number;
}

/** El precio de una fila, en sus dos lecturas equivalentes. */
export interface LinePricing {
    rate: number;
    /** (precio - costo) / costo — la lectura de los materiales. */
    markupPercent: number;
    /** (precio - costo) / precio — la lectura comparable, la que usa la cabecera. */
    marginPercent: number;
}

/** Margen utilizable: fuera de [0, 99) la división por (1 - m) explota o cambia de signo. */
function safeMarginFraction(targetMarginPercent: number): number {
    if (!Number.isFinite(targetMarginPercent)) return 0;
    return Math.min(Math.max(targetMarginPercent, 0), 99) / 100;
}

/** Markup utilizable: negativo significaría vender bajo costo sin decirlo. */
function safeMarkupPercent(materialMarkupPercent: number): number {
    if (!Number.isFinite(materialMarkupPercent)) return 0;
    return Math.max(materialMarkupPercent, 0);
}

/**
 * El precio de una partida a partir de su costo y su tipo.
 *
 * `cost <= 0` devuelve `rate: 0` —no hay precio que calcular todavía— pero
 * CONSERVA los porcentajes del dial. Esto no es un detalle: las calculadoras
 * sueltan filas sin costo a propósito ("teclea el costo por planta en el
 * presupuesto"), y cuando antes se devolvía `markupPercent: 0`, al teclear el
 * costo después `updateItem` multiplicaba por (1 + 0) y la fila se vendía AL
 * COSTO. 120 plantas a $18 eran $2.160 de venta y $0 de ganancia.
 */
export function priceLineByType(
    type: EstimateItemType | undefined,
    cost: number,
    dials: LinePricingDials,
): LinePricing {
    const safeCost = Number.isFinite(cost) ? cost : 0;

    // Pase directo: la regla vive en utils/passThroughPricing y no se discute aquí.
    if (isPassThroughType(type)) {
        return { rate: safeCost, markupPercent: 0, marginPercent: 0 };
    }

    if (type === 'Material') {
        const markupPercent = safeMarkupPercent(dials.materialMarkupPercent);
        const rate = safeCost > 0 ? safeCost * (1 + markupPercent / 100) : 0;
        return {
            rate,
            markupPercent,
            marginPercent: rate > 0 ? ((rate - safeCost) / rate) * 100 : 0,
        };
    }

    // Labor, Equipment, Subcontractor: margen sobre el precio.
    const marginFraction = safeMarginFraction(dials.targetMarginPercent);
    const rate = safeCost > 0 ? safeCost / (1 - marginFraction) : 0;
    return {
        rate,
        markupPercent: safeCost > 0 ? ((rate - safeCost) / safeCost) * 100 : 0,
        marginPercent: marginFraction * 100,
    };
}

/**
 * Igual que `priceLineByType`, pero además devuelve el `total` de la fila.
 * Azúcar para los siete sitios que construyen un EstimateItem completo.
 */
export function priceLine(
    type: EstimateItemType | undefined,
    cost: number,
    quantity: number,
    dials: LinePricingDials,
): LinePricing & { total: number } {
    const pricing = priceLineByType(type, cost, dials);
    const safeQty = Number.isFinite(quantity) ? quantity : 0;
    return { ...pricing, total: pricing.rate * safeQty };
}

/**
 * Las dos lecturas de un precio ya fijado (el usuario tecleó la tarifa a mano).
 * Se usa al re-derivar porcentajes, para que nadie vuelva a escribir la
 * división al revés.
 */
export function percentagesFromRate(cost: number, rate: number): { markupPercent: number; marginPercent: number } {
    const safeCost = Number.isFinite(cost) ? cost : 0;
    const safeRate = Number.isFinite(rate) ? rate : 0;
    return {
        markupPercent: safeCost > 0 ? ((safeRate - safeCost) / safeCost) * 100 : 0,
        marginPercent: safeRate > 0 ? ((safeRate - safeCost) / safeRate) * 100 : 0,
    };
}

/* ════════════════════════════════════════════════════════════════════════════
 *  Modelo v2: el precio en dos capas
 *
 *  Todo precio de línea se arma igual (PLAN-pricing-model-v2, Parte A.1):
 *
 *      baseRate = el precio que produjo la REGLA de la fila, a partir del costo
 *      rate     = baseRate × k        (k = factor de margen neto del estimado)
 *      total    = rate × cantidad
 *
 *  `baseRate` se guarda; `rate` se deriva. Las dos funciones de abajo son las
 *  dos mitades de esa frase: `repriceFromPin` produce el baseRate, `applyFactor`
 *  lo convierte en el rate. Ningún otro sitio de la app tiene permiso para
 *  derivar un precio de un costo por su cuenta — hoy once lo hacen, y no
 *  coinciden entre sí: eso es exactamente lo que este modelo viene a terminar.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * El precio final de una fila: su `baseRate` escalado por el factor global.
 *
 * El pase directo NUNCA escala. Un permiso de $400 sigue costando $400 aunque
 * el contratista suba su meta de margen al 45%: no es su trabajo, es el recibo
 * de un tercero, y el cliente puede leer la cifra en el papel del municipio.
 * Por eso el factor se calcula midiendo el margen SOBRE EL TRABAJO PROPIO
 * (Parte A.4) y por eso aquí se sale antes de multiplicar.
 *
 * `k` no finito o <= 0 cae a 1, el ancla honesta ("exactamente mis diales").
 * Un k de 0 pondría el estimado entero a cero sin avisar; ningún valor así
 * llega de un solver sano, y si llega, el fallo es del solver y no debe
 * traducirse en un documento de $0 delante del cliente.
 */
export function applyFactor(baseRate: number, k: number, type: EstimateItemType | undefined): number {
    const safeBase = Number.isFinite(baseRate) ? baseRate : 0;
    if (isPassThroughType(type)) return safeBase;
    const safeK = Number.isFinite(k) && k > 0 ? k : 1;
    return safeBase * safeK;
}

/** Lo que hace falta de una partida para recalcular su precio base. */
export interface PinnedLineInput {
    type?: EstimateItemType;
    cost: number;
    pricePinned?: boolean;
    pinnedPercent?: number;
    pinnedMetric?: 'markup' | 'margin' | 'rate';
}

/**
 * El `baseRate` de una fila: del PIN si el contratista fijó algo, y si no de la
 * política de precios del estimado.
 *
 * OJO CON EL NOMBRE DEL CAMPO: el `rate` que devuelve es el precio BASE, antes
 * del factor global. Se llama `rate` para reusar `LinePricing` y que los
 * porcentajes viajen con él; el precio que ve el cliente sale de pasarlo por
 * `applyFactor`.
 *
 * POR QUÉ EL PIN ES UNA LÍNEA BASE Y NO UN CANDADO (Parte A.3):
 *   El dueño pidió dos cosas a la vez —«que todo suba y baje junto» y «poder
 *   editar cada una»— y solo son compatibles si el pin manda en el precio
 *   RELATIVO y el dial en el NIVEL absoluto. Congelar filas además vuelve
 *   insoluble el solver en cuanto hay suficientes pines, lo que obliga a
 *   deslizadores deshabilitados y a pedirle al usuario que «quite el pin a 4
 *   líneas para continuar»: complejidad comprada con una promesa que el
 *   producto nunca hizo.
 *
 * LOS FEE NO ADMITEN PIN. Un pase directo va a costo y punto; fijarle un
 * porcentaje sería cobrarle al cliente por encima de un recibo que puede leer.
 * Si una fila vieja trae un pin de cuando era Material, aquí se ignora en vez
 * de respetarse — cambiar el tipo borra el pin (Parte A.3), y esta función no
 * puede ser la puerta de atrás por la que un pin muerto vuelve a cobrar.
 *
 * Un pin incompleto (marcado, pero sin metro o sin porcentaje utilizable) se
 * trata como si no lo hubiera: mejor volver al precio del taller que inventar
 * una base con datos a medias.
 */
export function repriceFromPin(item: PinnedLineInput, policy: LinePricingDials): LinePricing {
    const safeCost = Number.isFinite(item.cost) ? item.cost : 0;

    // El pase directo se resuelve antes de mirar el pin: no hay pin que valga.
    if (isPassThroughType(item.type)) {
        return priceLineByType(item.type, safeCost, policy);
    }

    const pinned = item.pricePinned === true && Number.isFinite(item.pinnedPercent as number);
    if (!pinned) {
        return priceLineByType(item.type, safeCost, policy);
    }

    const valor = item.pinnedPercent as number;

    // Tarifa tecleada entera: manda tal cual, incluso sin costo. Ese es el caso
    // de la partida de movilización de $500 con costo 0 — cotizarla por
    // porcentaje la dejaría en $0, y es una fila real que se cobra.
    if (item.pinnedMetric === 'rate') {
        const rate = Math.max(valor, 0);
        return { rate, ...percentagesFromRate(safeCost, rate) };
    }

    // Porcentaje tecleado. El metro decide la fórmula; nunca se deduce del tipo
    // al leer, porque un mismo 30 vale $26,00 como markup y $28,57 como margen.
    if (item.pinnedMetric === 'markup') {
        const markupPercent = safeMarkupPercent(valor);
        // Costo aún en 0: rate 0 pero el porcentaje SE CONSERVA. Si se devolviera
        // 0 aquí, al teclear el costo después la fila se vendería al costo — el
        // fallo de las 120 plantas a $18 con $0 de ganancia.
        const rate = safeCost > 0 ? safeCost * (1 + markupPercent / 100) : 0;
        return {
            rate,
            markupPercent,
            marginPercent: rate > 0 ? ((rate - safeCost) / rate) * 100 : 0,
        };
    }

    if (item.pinnedMetric === 'margin') {
        const marginFraction = safeMarginFraction(valor);
        const rate = safeCost > 0 ? safeCost / (1 - marginFraction) : 0;
        return {
            rate,
            markupPercent: safeCost > 0 ? ((rate - safeCost) / safeCost) * 100 : 0,
            marginPercent: marginFraction * 100,
        };
    }

    // Marcado como fijado pero sin metro: dato a medias, se ignora el pin.
    return priceLineByType(item.type, safeCost, policy);
}
